import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useEffect, useLayoutEffect, useReducer, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  fetchDataSuccess,
  appendListData,
  mergeListData,
  resetListData,
  setLastUpdate,
  setCurrentCollection,
  setLastDoc,
  setLoading,
  setSortOrder,
} from '../store/listSlice';
import { setIsLoadingSection } from '../store/coreSlice';
import moment from 'moment';
import nodeAxiosFirebase, {
  isCircuitOpen,
  throwIfErrorData,
} from '../utils/nodeAxiosFirebase';

const SOFT_REFRESH_THROTTLE_MS = 10 * 1000;
const FIVE_MINUTE_COLLECTIONS = new Set([
  'contacts', 'services', 'articles', 'profiles', 'grids',
  'cardsuninvoiced', 'cardsinvoiced', 'storages', 'tasks',
]);
const MAX_REFRESH_CONTEXTS = 100;
const softRefreshTimestamps = new Map();
// The realtime channel rebroadcasts every structure's events on each write, so
// the same events land again long after they were handled. Tracking the last
// handled signature keeps replays from retriggering a list fetch.
const handledEventSignatures = new Map();
let cacheGeneration = 0;

export const clearSoftRefreshTimestamps = () => {
  cacheGeneration += 1;
  softRefreshTimestamps.clear();
  handledEventSignatures.clear();
};

const EMPTY_SORT = { sortBy: null, sortOrder: null };

const getSessionKey = (state) => JSON.stringify([
  state.core.user?.uid || state.core.user?.id || null,
  state.core.user?.activeBusiness?.businessId || null,
  state.core.businessData?.id || null,
  Boolean(state.core.user?.sandboxMode?.enabled),
  Boolean(state.core.isDemoMode),
]);

function isCanceledError(error) {
  return (
    error?.name === 'CanceledError' ||
    error?.name === 'AbortError' ||
    error?.code === 'ERR_CANCELED'
  );
}

function sortsDiffer(a, b) {
  return a?.sortBy !== b?.sortBy || a?.sortOrder !== b?.sortOrder;
}

export const useModuleData = ({
  structureId,
  moduleId,
  activeTab = 0,
  page = 1,
  searchValue = '',
  orderBy = '',
  order,
  numericFilters = [],
  filterFacets = null,
  extraNumericFilters = [],
  segmentId = null,
  businessId = null,
  lang: requestedLang = 'fr',
  lastDoc = null,
  selectedDate = null,
  selectedView = 'none',
  sortField = null,
  rangeDates = null,
  rangeDatesDays = 30,
  enabled = true,
  selectedRangeEnd = null,
  collectionField = null,
  listType = 'list',
  insideBoundingBox = null,
  taskScope = null,
  currency = null,
}) => {
  const dispatch = useDispatch();
  const store = useStore();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const lang = requestedLang?.slice(0, 2) || 'fr';
  const sessionKey = useSelector(getSessionKey);
  const userId = useSelector((state) => state.core.user?.uid || state.core.user?.id);
  const activeBusinessId = useSelector((state) => state.core.user?.activeBusiness?.businessId);
  const isDemoMode = useSelector((state) => state.core.isDemoMode);
  const canRead = enabled && (isDemoMode || Boolean(userId)) &&
    (!activeBusinessId || activeBusinessId === businessId);
  const requestScopeRef = useRef(null);
  const softRefreshRequestRef = useRef(null);
  const pendingRefetchRef = useRef(null);
  const [sessionRevision, invalidateSession] = useReducer((revision) => revision + 1, 0);

  const realtimeSignature = useSelector((state) => {
    const metadata = state.list[moduleId || structureId]?.collectionData || {};
    const dependencies = Object.values(metadata).flatMap(entry => entry?.source?.dependencies || []);
    const events = [...new Set([structureId, ...dependencies])].flatMap(id => state.realtime.events[id] || []);
    if (!events?.length) return '';
    return events
      .map((event) => `${event?.id}:${event?.type}:${event?.timeStamp}`)
      .join('|');
  });
  const demoData = useSelector((state) => state.core.demoData);
  const demoDataRef = useRef(demoData);
  demoDataRef.current = demoData;
  const existingCollectionData = useSelector(
    (state) => state.list[moduleId || structureId]?.collectionData
  );
  const existingCollectionDataRef = useRef(existingCollectionData);
  existingCollectionDataRef.current = existingCollectionData;
  const businessModules = useSelector((state) => state.core.businessModules);
  const businessModulesRef = useRef(businessModules);
  businessModulesRef.current = businessModules;

  const stateKey = moduleId || structureId;
  const userSortBy = useSelector(
    (state) => state.list[moduleId || structureId]?.sortBy ?? null
  );
  const userSortOrder = useSelector(
    (state) => state.list[moduleId || structureId]?.sortOrder ?? null
  );
  const userSortRef = useRef(EMPTY_SORT);
  userSortRef.current = {
    sortBy: userSortBy ?? null,
    sortOrder: userSortOrder ?? null,
  };
  const lastAppliedSortRef = useRef(EMPTY_SORT);
  const lastHandledErrorAtRef = useRef(0);
  const hasLoadedList = useSelector((state) => {
    const listData = state.list[moduleId || structureId]?.data;
    return Boolean(listData && Object.keys(listData).length > 0);
  });

  // Calcul mémorisé des filtres de date pour éviter les re-renders inutiles
  const finalNumericFilters = useMemo(() => {
    let filters = numericFilters || [];

    // Pour les modules "passes", forcer le filtre de date basé sur selectedDate de Redux
    if (collectionField === 'passes' && selectedDate) {
      const currentSelectedDate = moment.unix(selectedDate);
      const currentRangeEnd = selectedRangeEnd
        ? moment.unix(selectedRangeEnd)
        : null;
      const dateField = sortField || 'targetDate';
      let dateFilters = [];

      if (currentRangeEnd) {
        const startTimestamp = currentSelectedDate.clone().startOf('day').unix();
        const endTimestamp = currentRangeEnd.clone().endOf('day').unix();
        dateFilters.push(`${dateField}:${startTimestamp} TO ${endTimestamp}`);
      } else {
        const startTimestamp = currentSelectedDate.clone().startOf('day').unix();
        const endTimestamp = currentSelectedDate.clone().endOf('day').unix();
        dateFilters.push(`${dateField}:${startTimestamp} TO ${endTimestamp}`);
      }

      const existingFilters = (numericFilters || []).filter(
        (filter) => !filter.includes(dateField + ':')
      );
      return [...existingFilters, ...dateFilters];
    }

    if (sortField && selectedDate && selectedView) {
      let dateFilters = [];
      const currentSelectedDate = moment.unix(selectedDate);
      const currentRangeEnd = selectedRangeEnd
        ? moment.unix(selectedRangeEnd)
        : null;

      if (selectedView === 'single' || rangeDates === 'single') {
        const rangeStart = currentSelectedDate.clone().startOf('day');
        const rangeEnd = currentSelectedDate.clone().endOf('day');
        const startTimestamp = rangeStart.unix();
        const endTimestamp = rangeEnd.unix();
        const filterString = `${sortField}:${startTimestamp} TO ${endTimestamp}`;
        dateFilters.push(filterString);
      } else if (selectedView === 'week' || rangeDates === 'week') {
        let rangeStart;
        let rangeEnd;

        if (currentRangeEnd) {
          rangeStart = currentSelectedDate.clone().startOf('day');
          rangeEnd = currentRangeEnd.clone().endOf('day');
        } else {
          const startOfWeek = currentSelectedDate
            .clone()
            .startOf('week')
            .add(1, 'day');
          rangeStart = startOfWeek.clone().startOf('day');
          rangeEnd = startOfWeek.clone().add(6, 'days').endOf('day');
        }

        const startTimestamp = rangeStart.unix();
        const endTimestamp = rangeEnd.unix();
        const filterString = `${sortField}:${startTimestamp} TO ${endTimestamp}`;
        dateFilters.push(filterString);
      } else if (selectedView === 'multi' || rangeDates === 'multi') {
        if (currentRangeEnd) {
          const startTimestamp = currentSelectedDate
            .clone()
            .startOf('day')
            .unix();
          const endTimestamp = currentRangeEnd.clone().endOf('day').unix();
          const filterString = `${sortField}:${startTimestamp} TO ${endTimestamp}`;
          dateFilters.push(filterString);
        } else {
          const startOfMonth = currentSelectedDate.clone().startOf('month');
          const endOfMonth = currentSelectedDate.clone().endOf('month');
          const startTimestamp = startOfMonth.startOf('day').unix();
          const endTimestamp = endOfMonth.endOf('day').unix();
          const filterString = `${sortField}:${startTimestamp} TO ${endTimestamp}`;
          dateFilters.push(filterString);
        }
      } else if (selectedView === 'rolling' || rangeDates === 'rolling') {
        const days = Number(rangeDatesDays) || 30;
        const rangeStart = currentSelectedDate
          .clone()
          .subtract(days, 'days')
          .startOf('day');
        const rangeEnd = (currentRangeEnd || currentSelectedDate)
          .clone()
          .endOf('day');
        const filterString = `${sortField}:${rangeStart.unix()} TO ${rangeEnd.unix()}`;
        dateFilters.push(filterString);
      }

      const existingFilters = (numericFilters || []).filter(
        (filter) => !filter.includes(sortField + ':')
      );
      filters = [...existingFilters, ...dateFilters];
    }

    if (extraNumericFilters?.length > 0) {
      filters = [...filters, ...extraNumericFilters];
    }

    return filters;
  }, [
    numericFilters,
    extraNumericFilters,
    sortField,
    rangeDates,
    rangeDatesDays,
    selectedDate,
    selectedView,
    selectedRangeEnd,
    collectionField,
  ]);

  // Génération mémorisée de la clé de query (sans page pour infinite scroll)
  const queryKey = useMemo(
    () => [
      'list-data',
      structureId,
      moduleId,
      segmentId || 'no-segment',
      activeTab,
      businessId,
      orderBy === 'default' ? '' : orderBy || '',
      order,
      JSON.stringify(finalNumericFilters),
      filterFacets ? JSON.stringify(filterFacets) : 'no-facets',
      `search:${searchValue || 'none'}`,
      insideBoundingBox ? JSON.stringify(insideBoundingBox) : 'no-bounds',
      isDemoMode ? 'demo' : 'live',
      taskScope || 'no-scope',
      currency || 'default-currency',
      lang,
      sessionKey,
      sessionRevision,
      canRead,
    ],
    [
      structureId,
      moduleId,
      activeTab,
      searchValue,
      orderBy,
      order,
      finalNumericFilters,
      filterFacets,
      segmentId,
      businessId,
      insideBoundingBox,
      isDemoMode,
      taskScope,
      currency,
      lang,
      sessionKey,
      sessionRevision,
      canRead,
    ]
  );

  // A distinct scope for each committed view prevents A -> B -> A responses
  // from reviving an old request. Read the store too: account switches can be
  // dispatched before React commits the next render.
  const requestScopeKey = JSON.stringify([queryKey, canRead]);
  const requestContext = useMemo(() => ({ key: requestScopeKey, sessionRevision }), [requestScopeKey, sessionRevision]);
  useLayoutEffect(() => {
    const scope = { context: requestContext, sessionKey, active: true, controllers: new Set() };
    requestScopeRef.current = scope;
    const invalidateScope = () => {
      scope.active = false;
      scope.controllers.forEach((controller) => controller.abort());
      const pending = softRefreshRequestRef.current;
      if (pending?.scope === scope) {
        pending.controller.abort();
        softRefreshRequestRef.current = null;
      }
      if (pendingRefetchRef.current?.scope === scope) pendingRefetchRef.current = null;
    };
    const unsubscribe = store.subscribe(() => {
      if (scope.active && getSessionKey(store.getState()) !== scope.sessionKey) {
        invalidateScope();
        invalidateSession();
      }
    });
    return () => {
      invalidateScope();
      unsubscribe();
    };
  }, [requestContext, sessionKey, store]);

  const isCurrentRequest = useCallback((scope, generation) => (
    scope?.active && scope === requestScopeRef.current &&
    scope.context === requestContext && generation === cacheGeneration &&
    getSessionKey(store.getState()) === scope.sessionKey
  ), [requestContext, store]);

  const queryFn = useCallback(
    async ({ pageParam = null, signal }) => {
      const scope = requestScopeRef.current;
      const generation = cacheGeneration;
      if (!canRead || signal?.aborted || !isCurrentRequest(scope, generation)) {
        throw new DOMException('Request canceled', 'AbortError');
      }
      const cachedQuery = queryClient.getQueryState(queryKey);
      // Infinite-query refetches read every retained page. Commit their rows
      // together so the list does not shrink after the first response.
      const deferListUpdate = Boolean(cachedQuery?.data?.pages?.length && !cachedQuery.fetchMeta?.fetchMore);
      if (deferListUpdate && pendingRefetchRef.current?.signal !== signal) {
        const cached = cachedQuery.data;
        const current = store.getState().list[stateKey];
        const field = cached.pages[0]?.collectionField;
        const existing = current?.data?.[field] || [];
        const firstRow = cached.pages[0]?.data?.[0];
        const firstIndex = firstRow ? existing.findIndex(row =>
          (row.rowKey || row.id) === (firstRow.rowKey || firstRow.id)) : -1;
        const sameContext = current?.collectionData?.[field]?.requestScopeKey === requestScopeKey;
        pendingRefetchRef.current = {
          scope, generation, signal,
          // Redux also retains previously loaded pages outside maxPages.
          prefix: sameContext && cached.pageParams[0] != null && firstIndex > 0
            ? existing.slice(0, firstIndex) : [],
        };
      } else if (!deferListUpdate) {
        pendingRefetchRef.current = null;
      }
      if (isDemoMode && demoDataRef.current?.[structureId] && !pageParam) {
        const demo = demoDataRef.current[structureId];
        const activeCollectionField = collectionField || demo.collectionField || 'contacts';
        const headers = demo.headers || [];

        const data = (demo.list || []).map((doc) => {
          const values = headers.map((h) => {
            const key = h.value;
            const raw = doc[key] ?? doc?.data?.[key] ?? '';
            let transformedValue = raw;

            const selections = h.selections || [];
            const type = h.typeValue || '';
            const isSelection = selections.length > 0 ||
              ['selection', 'dropdown', 'status', 'select'].includes(type);

            if (isSelection && selections.length > 0) {
              if (Array.isArray(raw)) {
                transformedValue = raw
                  .map((v) => selections.find((s) => String(s.value) === String(v))?.label || v)
                  .join(', ');
              } else {
                const match = selections.find((s) => String(s.value) === String(raw));
                transformedValue = match?.label || raw;
              }
            } else if (raw && typeof raw === 'object' && raw._seconds) {
              const d = new Date(raw._seconds * 1000);
              transformedValue = d.toLocaleDateString(lang || 'fr', {
                year: 'numeric', month: 'short', day: 'numeric',
              });
            } else if (Array.isArray(raw)) {
              transformedValue = raw.join(', ');
            } else if (typeof raw === 'number') {
              transformedValue = String(raw);
            } else if (typeof raw === 'boolean') {
              transformedValue = raw ? 'Oui' : 'Non';
            }

            return {
              value: key,
              transformedValue: transformedValue ?? '',
              typeValue: h.typeValue || 'string',
              weight: h.fontWeigth || 'regular',
              selections: h.selections || [],
            };
          });

          return {
            ...doc,
            path: activeCollectionField + '/' + doc.id,
            values,
          };
        });

        dispatch(setLoading({ structureId: stateKey, loading: false }));
        dispatch(
          fetchDataSuccess({
            structureId: stateKey,
            data,
            collectionField: activeCollectionField,
            realStructureId: structureId,
            moduleId,
          })
        );
        const prevCollection = existingCollectionDataRef.current?.[activeCollectionField] || {};
        const moduleMeta = businessModulesRef.current?.find((m) => m?.id === moduleId) || {};

        let tabs = prevCollection.tabs?.length > 0
          ? prevCollection.tabs
          : moduleMeta?.list?.tabs?.length > 0
            ? moduleMeta.list.tabs
            : [];

        if (tabs.length === 0 && moduleMeta?.listType) {
          tabs = [{
            listType: moduleMeta.listType,
            sortField: moduleMeta.sortField || 'lastUpdate',
            sort: moduleMeta.sort || 'desc',
            action: 'element',
            name: '',
          }];
        }

        if (demo.statuses?.length > 0) {
          tabs = tabs.map((tab) => ({
            ...tab,
            statuses: tab.statuses?.length > 0 ? tab.statuses : demo.statuses,
          }));
        }

        dispatch(
          setCurrentCollection({
            structureId: stateKey,
            collectionData: {
              ...prevCollection,
              requestScopeKey,
              lang,
              name: demo.structureName || prevCollection.name || '',
              nbHits: data.length,
              nbDocuments: data.length,
              totalDocuments: data.length,
              activeCollection: activeCollectionField,
              collection: activeCollectionField,
              structureId,
              header: demo.headers || [],
              currentPage: 1,
              tabs,
            },
            collectionField: activeCollectionField,
          })
        );

        return {
          data,
          lastVisible: null,
          nbHits: data.length,
          totalDocuments: data.length,
          collectionField: activeCollectionField,
          hasMore: false,
        };
      }

      const fetchParams = {
        structureId,
        moduleId,
        lang,
        activeTab,
        searchValue: searchValue || undefined,
        page: 1,
        numericFilters: finalNumericFilters,
        order,
        segmentId,
      };

      // Ne pas envoyer orderBy si c'est 'default' (placeholder)
      if (orderBy && orderBy !== 'default') {
        fetchParams.orderBy = orderBy;
      }

      // Utiliser pageParam comme startAfter pour les pages suivantes
      if (pageParam) {
        fetchParams.startAfter = pageParam;
      }

      // Update loading state
      dispatch(setIsLoadingSection(moduleId));
      dispatch(setLoading({ structureId: stateKey, loading: true }));

      let body = {
        lang: lang?.slice(0, 2) || 'fr',
        moduleId: moduleId,
        structureId: structureId,
        activeTab: activeTab,
      };

      if (searchValue) {
        body.query = searchValue;
      } else if (filterFacets?.length > 0 || extraNumericFilters?.length > 0) {
        body.query = '';
      }
      if (finalNumericFilters?.length > 0) {
        body.numericFilters = finalNumericFilters;
      }
      if (orderBy && orderBy !== 'default') {
        body.orderBy = orderBy;
      }
      if (order) {
        body.order = order;
      }
      if (segmentId) {
        body.segmentId = segmentId;
      }
      if (pageParam) {
        body.startAfter = pageParam;
      }
      if (insideBoundingBox) {
        body.insideBoundingBox = insideBoundingBox;
      }
      if (filterFacets?.length > 0) {
        body.filterFacets = filterFacets;
      }
      if (taskScope) {
        body.taskScope = taskScope;
      }
      if (currency) body.currency = currency;

      // La base de données limite les plages de dates à 30 jours :
      // si le filtre dépasse, on split en deux appels et on merge les résultats
      const MAX_RANGE_SECONDS = 30 * 24 * 3600;
      let splitInfo = null;
      if (!pageParam && sortField && body.numericFilters?.length > 0) {
        const dateFilterRegex = new RegExp(`^${sortField}:(\\d+) TO (\\d+)$`);
        const idx = body.numericFilters.findIndex((f) =>
          dateFilterRegex.test(String(f))
        );
        if (idx !== -1) {
          const [, startStr, endStr] = String(body.numericFilters[idx]).match(
            dateFilterRegex
          );
          const start = Number(startStr);
          const end = Number(endStr);
          if (end - start > MAX_RANGE_SECONDS) {
            splitInfo = { idx, start, end, mid: start + MAX_RANGE_SECONDS };
          }
        }
      }

      let response;
      const controller = new AbortController();
      const abort = () => controller.abort();
      scope.controllers.add(controller);
      signal?.addEventListener('abort', abort, { once: true });
      try {
        if (splitInfo) {
        const makeBody = (s, e) => ({
          ...body,
          numericFilters: body.numericFilters.map((f, i) =>
            i === splitInfo.idx ? `${sortField}:${s} TO ${e}` : f
          ),
        });
        const [resA, resB] = await Promise.all([
          nodeAxiosFirebase({
            method: 'POST',
            url: 'bosV4/list',
            body: makeBody(splitInfo.start, splitInfo.mid),
            signal: controller.signal,
          }),
          nodeAxiosFirebase({
            method: 'POST',
            url: 'bosV4/list',
            body: makeBody(splitInfo.mid + 1, splitInfo.end),
            signal: controller.signal,
          }),
        ]);
        throwIfErrorData(resA);
        throwIfErrorData(resB);
        const seenIds = new Set();
        const mergedHits = [...(resA?.hits || []), ...(resB?.hits || [])].filter(
          (hit) => {
            if (!hit?.id || seenIds.has(hit.id)) return false;
            seenIds.add(hit.id);
            return true;
          }
        );
        response = {
          ...resA,
          hits: mergedHits,
          nbHits: mergedHits.length,
          totalHits: (resA?.totalHits || 0) + (resB?.totalHits || 0),
          lastVisible: null,
        };
      } else {
        response = throwIfErrorData(
          await nodeAxiosFirebase({
            method: 'POST',
            url: 'bosV4/list',
            body: body,
            signal: controller.signal,
          })
        );
      }

      // Cancellation is also checked after the transport: an old response must
      // never repopulate Redux after navigation, logout or a cache reset.
      if (controller.signal.aborted || !isCurrentRequest(scope, generation)) {
        throw new DOMException('Request canceled', 'AbortError');
      }

      const data = response?.hits?.map((doc) => ({
        ...doc,
        path:
          response?.collection === 'connections'
            ? doc?.documentPath || doc?.path
            : response?.collection + '/' + doc?.id,
        id: doc?.id,
      }));

      // Initial reads and pagination keep the existing Redux path.
      if (!deferListUpdate && pageParam) {
        // Append data for subsequent pages
        dispatch(
          appendListData({
            structureId: stateKey,
            data,
            collectionField: response?.collectionField,
          })
        );
      } else if (!deferListUpdate) {
        // Replace data for first page
        dispatch(
          fetchDataSuccess({
            structureId: stateKey,
            data,
            collectionField: response?.collectionField,
            realStructureId: structureId,
            moduleId,
          })
        );
      }

      // Mettre à jour lastDoc
      if (!deferListUpdate && data?.length > 0) {
        dispatch(
          setLastDoc({ structureId: stateKey, lastDoc: response?.lastVisible })
        );
      }

      // Mettre à jour collectionData
      const objectIDsDoc = data?.map((doc) => doc.id);
      const firstTen = objectIDsDoc?.slice(0, 10);
      const hitsTotal = response?.totalHits;

      const collectionDataResponse = {
        requestScopeKey,
        lang,
        name: response?.name || response?.collectionField,
        nbHits: response?.nbHits || 0,
        nbDocuments: response?.hits?.length || 0,
        totalDocuments: hitsTotal,
        facets: response?.facets,
        source: response?.source,
        activeCollection: response?.collectionField,
        collection: response?.collectionField,
        structureId: structureId,
        header: response?.header,
        summary: response?.summary,
        currentPage: 1,
        queryID: response?.queryID,
        objectIDs: firstTen,
        tabs: response?.tabs ?? response?.preferences?.tabs,
        edit: response?.edit ?? response?.preferences?.edit ?? false,
        email: response?.email ?? response?.preferences?.email ?? false,
      };

      if (!deferListUpdate) dispatch(
        setCurrentCollection({
          structureId: stateKey,
          collectionData: collectionDataResponse,
          collectionField: response?.collectionField,
        })
      );

      return {
        data,
        collectionData: collectionDataResponse,
        lastVisible: response?.lastVisible,
        nbHits: response?.nbHits || 0,
        totalDocuments: hitsTotal,
        collectionField: response?.collectionField,
        hasMore: data?.length > 0 && response?.lastVisible,
      };
      } catch (error) {
        if (pendingRefetchRef.current?.signal === signal) pendingRefetchRef.current = null;
        throw error;
      } finally {
        scope.controllers.delete(controller);
        signal?.removeEventListener('abort', abort);
        if (!controller.signal.aborted && isCurrentRequest(scope, generation)) {
          dispatch(setIsLoadingSection(null));
          dispatch(setLoading({ structureId: stateKey, loading: false }));
        }
      }
    },
    [
      structureId,
      moduleId,
      lang,
      activeTab,
      searchValue,
      finalNumericFilters,
      filterFacets,
      extraNumericFilters.length,
      order,
      segmentId,
      orderBy,
      dispatch,
      stateKey,
      insideBoundingBox,
      isDemoMode,
      collectionField,
      sortField,
      taskScope,
      currency,
      canRead,
      isCurrentRequest,
      queryClient,
      queryKey,
      requestScopeKey,
      store,
    ]
  );

  const staleTime = FIVE_MINUTE_COLLECTIONS.has(collectionField) &&
    listType !== 'kanban' && listType !== 'calendar'
    ? 5 * 60 * 1000 : 60 * 1000;

  // React Query with Infinite
  const query = useInfiniteQuery({
    queryKey,
    queryFn,
    enabled:
      canRead &&
      Boolean(structureId) &&
      Boolean(moduleId) &&
      Boolean(businessId),
    staleTime,
    gcTime: 1000 * 60 * 10,
    maxPages: 5,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: (failureCount, err) => {
      if (isCanceledError(err)) return false;
      const status = err?.status;
      if (status === 401 || status === 403) return false;
      return failureCount < 1;
    },
    refetchOnReconnect: false,
    getNextPageParam: (lastPage) => {
      // Retourne lastVisible si il y a plus de données
      if (lastPage?.hasMore && lastPage?.lastVisible) {
        return lastPage.lastVisible;
      }
      return undefined;
    },
  });

  useLayoutEffect(() => {
    const scope = requestScopeRef.current;
    if (!canRead || isDemoMode || query.isPlaceholderData || !query.data?.pages?.length ||
        !isCurrentRequest(scope, cacheGeneration)) return;
    const pages = query.data.pages;
    const lastPage = pages[pages.length - 1];
    const metadata = lastPage.collectionData;
    if (metadata?.requestScopeKey !== requestScopeKey) return;

    const pending = pendingRefetchRef.current;
    const completedRefetch = pending?.scope === scope && !pending.signal?.aborted &&
      isCurrentRequest(scope, pending.generation) && query.isSuccess && !query.isFetching;
    const currentMetadata = store.getState().list[stateKey]?.collectionData?.[lastPage.collectionField];
    // Reusing a fresh query does not call queryFn. Restore its own rows and
    // headers when returning from another search/filter in the same Module.
    if (!completedRefetch && currentMetadata?.requestScopeKey === requestScopeKey) return;

    const refreshedIds = new Set();
    const rows = pages.flatMap(page => page.data || []).filter(row => {
      const id = row.rowKey || row.id;
      if (refreshedIds.has(id)) return false;
      refreshedIds.add(id);
      return true;
    });
    const prefix = completedRefetch ? pending.prefix.filter(row => !refreshedIds.has(row.rowKey || row.id)) : [];
    dispatch(fetchDataSuccess({ structureId: stateKey, data: [...prefix, ...rows],
      collectionField: lastPage.collectionField, realStructureId: structureId, moduleId }));
    dispatch(setCurrentCollection({ structureId: stateKey, collectionField: lastPage.collectionField,
      collectionData: metadata }));
    dispatch(setLastDoc({ structureId: stateKey, lastDoc: lastPage.lastVisible }));
    if (completedRefetch) pendingRefetchRef.current = null;
  }, [canRead, isDemoMode, query.data, query.dataUpdatedAt, query.isPlaceholderData,
    query.isSuccess, query.isFetching, requestScopeKey, isCurrentRequest, store,
    stateKey, structureId, moduleId, dispatch]);

  const loadedLang = existingCollectionData?.[
    collectionField || query.data?.pages?.[0]?.collectionField
  ]?.lang;

  useEffect(() => {
    // Redux holds one language per module. Returning to a cached language
    // must reload it from page one, including after navigating away and back.
    if (canRead && !isDemoMode && loadedLang !== lang && queryClient.getQueryData(queryKey)) {
      queryClient.resetQueries({ queryKey, exact: true });
    }
  }, [canRead, isDemoMode, loadedLang, lang, queryKey, queryClient]);

  // Fonction de refresh manuel
  const refresh = useCallback(() => {
    // Reset les données dans Redux avant de refetch
    dispatch(resetListData({ structureId: stateKey }));
    return queryClient.invalidateQueries({
      queryKey: ['list-data', structureId, moduleId],
    });
  }, [queryClient, structureId, moduleId, dispatch, stateKey]);

  // Invalidation complète du cache du module (sans reset pour éviter le flash)
  const invalidate = useCallback(() => {
    return queryClient.invalidateQueries({
      queryKey: ['list-data', structureId, moduleId],
    });
  }, [queryClient, structureId, moduleId]);

  // Soft refresh: fetch fresh data and merge with existing (preserves scroll/pagination)
  const runSoftRefresh = useCallback(async (request) => {
    const { scope, generation, controller } = request;
    const isCurrent = () => !controller.signal.aborted && isCurrentRequest(scope, generation);
    if (!isCurrent()) return { success: false, canceled: true };
    try {
      if (Object.values(existingCollectionDataRef.current || {}).some(entry => entry?.source?.provider)) {
        // Re-query a projection from its first page: retaining old rows could keep
        // a paid invoice or a newly hidden contact visible after a native event.
        await queryClient.resetQueries({ queryKey, exact: true });
        return isCurrent() ? { success: true } : { success: false, canceled: true };
      }

      let body = {
        lang: lang?.slice(0, 2) || 'fr',
        moduleId: moduleId,
        structureId: structureId,
        activeTab: activeTab,
      };

      if (searchValue) {
        body.query = searchValue;
      } else if (filterFacets?.length > 0 || extraNumericFilters?.length > 0) {
        body.query = '';
      }
      if (finalNumericFilters?.length > 0) {
        body.numericFilters = finalNumericFilters;
      }
      if (orderBy && orderBy !== 'default') {
        body.orderBy = orderBy;
      }
      if (order) {
        body.order = order;
      }
      if (segmentId) {
        body.segmentId = segmentId;
      }
      if (taskScope) {
        body.taskScope = taskScope;
      }
      if (currency) body.currency = currency;
      if (filterFacets?.length > 0) body.filterFacets = filterFacets;
      if (insideBoundingBox) body.insideBoundingBox = insideBoundingBox;

      const response = throwIfErrorData(
        await nodeAxiosFirebase({
          method: 'POST',
          url: 'bosV4/list',
          body: body,
          signal: controller.signal,
        })
      );

      if (!isCurrent()) return { success: false, canceled: true };

      const data = response?.hits?.map((doc) => ({
        ...doc,
        path:
          response?.collection === 'connections'
            ? doc?.documentPath || doc?.path
            : response?.collection + '/' + doc?.id,
        id: doc?.id,
      }));

      // Merge data instead of replacing - preserves pagination state
      dispatch(
        (response?.source ? fetchDataSuccess : mergeListData)({
          structureId: stateKey,
          data,
          collectionField: response?.collectionField,
        })
      );

      // Update collection metadata
      const collectionDataResponse = {
        requestScopeKey,
        lang,
        name: response?.name || response?.collectionField,
        nbHits: response?.nbHits || 0,
        nbDocuments: response?.hits?.length || 0,
        totalDocuments: response?.totalHits,
        facets: response?.facets,
        source: response?.source,
        activeCollection: response?.collectionField,
        collection: response?.collectionField,
        structureId: structureId,
        header: response?.header,
        summary: response?.summary,
        currentPage: 1,
        queryID: response?.queryID,
        objectIDs: data?.slice(0, 10)?.map((doc) => doc.id),
        tabs: response?.tabs ?? response?.preferences?.tabs,
        edit: response?.edit ?? response?.preferences?.edit ?? false,
        email: response?.email ?? response?.preferences?.email ?? false,
      };

      dispatch(
        setCurrentCollection({
          structureId: stateKey,
          collectionData: collectionDataResponse,
          collectionField: response?.collectionField,
        })
      );

      // This merge only refreshes Redux, not every cached page/filter. Prevent
      // a later return from treating a pre-save query as fresh for five minutes.
      await queryClient.invalidateQueries({
        queryKey: ['list-data', structureId, moduleId],
        refetchType: 'none',
      });
      return { success: true, data };
    } catch (error) {
      if (!isCurrent() || isCanceledError(error)) return { success: false, canceled: true };
      console.error('[softRefresh] Error:', error);
      return { success: false, error };
    }
  }, [
    structureId,
    moduleId,
    lang,
    activeTab,
    searchValue,
    finalNumericFilters,
    orderBy,
    order,
    segmentId,
    dispatch,
    stateKey,
    taskScope,
    currency,
    queryClient,
    queryKey,
    filterFacets,
    extraNumericFilters,
    insideBoundingBox,
    isCurrentRequest,
    requestScopeKey,
  ]);

  const softRefresh = useCallback(() => {
    const scope = requestScopeRef.current;
    const generation = cacheGeneration;
    if (!canRead || !structureId || !moduleId || !businessId || !isCurrentRequest(scope, generation)) {
      return Promise.resolve({ success: false, canceled: true });
    }
    const pending = softRefreshRequestRef.current;
    if (pending?.scope === scope && pending.generation === generation) {
      // Calls in the same tick share one read. Calls after it started may
      // follow a new save: retain one trailing read for that whole burst.
      if (pending.started) pending.again = true;
      return pending.promise;
    }
    pending?.controller.abort();
    const request = { scope, generation, controller: new AbortController(), started: false, again: false };
    scope.controllers.add(request.controller);
    softRefreshRequestRef.current = request;
    request.promise = Promise.resolve().then(async () => {
      let result;
      do {
        request.again = false;
        request.started = true;
        result = await runSoftRefresh(request);
      } while (request.again && !request.controller.signal.aborted && isCurrentRequest(scope, generation));
      return result;
    }).finally(() => {
      scope.controllers.delete(request.controller);
      if (softRefreshRequestRef.current === request) softRefreshRequestRef.current = null;
    });
    return request.promise;
  }, [canRead, structureId, moduleId, businessId, isCurrentRequest, runSoftRefresh]);

  useEffect(() => {
    if (!canRead || !realtimeSignature || !structureId || !moduleId || !query.isSuccess) {
      return undefined;
    }

    const key = requestScopeKey;
    if (handledEventSignatures.get(key) === realtimeSignature) return undefined;

    const run = () => {
      if (isCircuitOpen()) return;
      // Each filter/view has its own replay state; bound these contexts as well
      // as the query cache when users visit many combinations in one session.
      handledEventSignatures.delete(key);
      softRefreshTimestamps.delete(key);
      handledEventSignatures.set(key, realtimeSignature);
      softRefreshTimestamps.set(key, Date.now());
      if (handledEventSignatures.size > MAX_REFRESH_CONTEXTS) {
        const oldest = handledEventSignatures.keys().next().value;
        handledEventSignatures.delete(oldest);
        softRefreshTimestamps.delete(oldest);
      }
      softRefresh();
    };

    const elapsed = Date.now() - (softRefreshTimestamps.get(key) || 0);
    if (elapsed >= SOFT_REFRESH_THROTTLE_MS) {
      run();
      return undefined;
    }

    // Defer instead of dropping: the event is real, only too close to the
    // previous refresh.
    const timer = setTimeout(run, SOFT_REFRESH_THROTTLE_MS - elapsed);
    return () => clearTimeout(timer);
  }, [canRead, realtimeSignature, structureId, moduleId, softRefresh, query.isSuccess, requestScopeKey]);

  // Update last update timestamp in Redux when data is successfully fetched
  useEffect(() => {
    if (query.isSuccess) {
      lastAppliedSortRef.current = { ...userSortRef.current };
      const timestamp = query.dataUpdatedAt
        ? Math.floor(query.dataUpdatedAt / 1000)
        : moment().unix();

      dispatch(
        setLastUpdate({
          structureId: stateKey,
          lastUpdate: timestamp,
        })
      );
    }
  }, [query.isSuccess, query.dataUpdatedAt, dispatch, stateKey]);

  // Sort is committed in Redux before the list request returns. If that
  // request fails, restore the last applied sort so the arrow does not
  // claim an order the server never served.
  useEffect(() => {
    if (!query.isError || query.isFetching || isCanceledError(query.error)) {
      return;
    }
    if (lastHandledErrorAtRef.current === query.errorUpdatedAt) return;
    lastHandledErrorAtRef.current = query.errorUpdatedAt;

    const applied = lastAppliedSortRef.current;
    const current = userSortRef.current;
    if (sortsDiffer(current, applied)) {
      toast.error(t('listSortError'));
      dispatch(
        setSortOrder({
          structureId: stateKey,
          sortBy: applied.sortBy,
          sortOrder: applied.sortOrder,
        })
      );
      return;
    }

    // First-load errors keep the full-page state. Later failures keep the
    // previous rows, so they need an explicit toast.
    if (hasLoadedList) {
      toast.error(t('listLoadError'));
    }
  }, [
    query.isError,
    query.isFetching,
    query.error,
    query.errorUpdatedAt,
    hasLoadedList,
    dispatch,
    stateKey,
    t,
  ]);

  return {
    queryData: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    isError: query.isError,
    error: query.error,
    isSuccess: query.isSuccess,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refresh,
    invalidate,
    softRefresh,
    refetch: query.refetch,
  };
};

export default useModuleData;
